import clueRegistry from "../../../tools/data/clues.json";
import type { Evaluation, Layout, Lesson } from "../types";

/** A stage made in the stage editor (/create): its board plus the three clues, kept in this browser. */
export interface CustomStage {
  key: string;
  layout: Layout;
  family: "noun" | "verb" | "adjective";
  shots: number;
  clues: Array<{ text: string; ja: string }>;
}

const STORAGE_KEY = "wordphysics.customStages";
const POWERS = ["heavy", "speed", "blast"] as const;
export const CUSTOM_PREFIX = "custom:";

export function loadCustomStages(): CustomStage[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved) as CustomStage[];
  } catch { /* start empty */ }
  return [];
}

export function saveCustomStages(stages: CustomStage[]) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(stages)); } catch { /* storage is optional */ }
}

/** Clues already scored in tools/data/matrix.json, keyed by their text. */
export const scoredClues = new Map(clueRegistry.clues.map((clue) => [clue.text, clue]));

export function customLesson(stage: CustomStage, index: number): Lesson {
  return {
    id: `custom-${stage.key}`,
    code: `CUSTOM ${index + 1}`,
    layout_id: stage.layout.id,
    grade: "elementary",
    shots: stage.shots,
    clue_set: {
      id: `${CUSTOM_PREFIX}${stage.key}`,
      family: stage.family,
      clues: stage.clues.map((clue, power) => ({ id: scoredClues.get(clue.text.trim())?.id ?? "unscored", text: clue.text.trim(), power: POWERS[power] })),
    },
  };
}

export function customLayout(layoutId: string): Layout | undefined {
  return loadCustomStages().find((stage) => stage.layout.id === layoutId)?.layout;
}

type TierTable = { clues: string[]; words: Record<string, [string[], string]> };
let tierTable: Promise<TierTable> | null = null;

/**
 * Scores a word from customTiers.json (npm run build-custom-tiers), a tier-only copy of the matrix.
 * It loads on first use, and carries no reasons. Returns null when the word or any clue is missing.
 */
export async function evaluateCustom(word: string, lesson: Lesson): Promise<Evaluation | null> {
  tierTable ??= import("./customTiers.json").then((module) => module.default as unknown as TierTable);
  const table = await tierTable;
  const entry = table.words[word];
  const columns = lesson.clue_set.clues.map((clue) => table.clues.indexOf(clue.id));
  if (!entry || columns.some((column) => column < 0)) return null;
  return { word, source: "seed", detected_parts_of_speech: entry[0], tiers: columns.map((column) => Number(entry[1][column])), reasons: [] };
}
